import React, { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

const SCROLL_THRESHOLD = 400;

// A "back to top" control that only reveals when the mouse hovers the left
// edge of the screen, and hides again once the mouse moves away — distinct
// from ScrollToTop.jsx, which is the invisible behavior that resets scroll
// position on route changes, not a clickable control.
export default function ScrollToTopButton() {
  const [scrolled, setScrolled] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Don't leave it stuck "open" if the page scrolls back to top while hovered.
  useEffect(() => {
    if (!scrolled) setHovering(false);
  }, [scrolled]);

  if (!scrolled) return null;

  const scrollTop = () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

  return (
    // Hover-detection zone: a comfortably-sized but unobtrusive strip hugging
    // the left edge, narrow enough to stay clear of page content (which sits
    // inside a max-width/padded container on every page here), desktop-only
    // since hover isn't a touch concept. Anchored with top+bottom (not a
    // fixed height centered on the viewport) so it can never drift down into
    // WhatsAppButton's bottom-left corner (fixed bottom-6 left-6) on shorter
    // viewports — the two were overlapping there, and WhatsApp's higher
    // z-index was winning and blocking hover/clicks on this button.
    <div
      data-testid="scroll-to-top-hotzone"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="hidden md:flex fixed left-0 top-28 bottom-36 w-9 z-40 items-center"
    >
      <button
        onClick={scrollTop}
        data-testid="scroll-to-top-btn"
        aria-label="Scroll to top"
        tabIndex={hovering ? 0 : -1}
        className={`ml-1.5 w-11 h-11 rounded-full bg-eduhu-dark text-eduhu-gold shadow-lg flex items-center justify-center transition-all duration-300 ease-out hover:bg-eduhu-claret ${
          hovering ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6 pointer-events-none"
        }`}
      >
        <ArrowUp size={18} strokeWidth={2} />
      </button>
    </div>
  );
}
